import useSWR, { preload } from 'swr';
import { useCallback, useEffect } from 'react';
import { useAuth } from '../auth';
import { useImagePreloader } from '../common/useImagePreloader';
import { getProfilePictureApi } from 'src/api';

const fetcher = async () => {
  const response = await getProfilePictureApi();
  return response?.profilePictureURL ?? null;
};

export const preloadProfilePicture = () => preload(['profilePicture'], fetcher);

export const useProfilePicture = () => {
  const { isAuthenticated } = useAuth();
  const { preloadImage } = useImagePreloader();

  const { data, error, isLoading, mutate } = useSWR<string | null>(
    isAuthenticated ? ['profilePicture'] : null,
    fetcher
  );

  const profilePictureURL = data ?? null;

  // Preload image when URL changes
  useEffect(() => {
    if (profilePictureURL) {
      preloadImage(profilePictureURL).catch(() => {});
    }
  }, [profilePictureURL, preloadImage]);

  const refreshProfilePicture = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const updateProfilePictureCache = useCallback(async (url: string) => {
    if (url) {
      await preloadImage(url).catch(() => {});
    }
    await mutate(url, false);
  }, [mutate, preloadImage]);

  const clearProfilePictureCache = useCallback(async () => {
    await mutate(null, false);
  }, [mutate]);

  return {
    profilePictureURL,
    loading: isLoading,
    error,
    refreshProfilePicture,
    updateProfilePictureCache,
    clearProfilePictureCache,
  };
};
